import React from "react";
import { TableLink } from "../../atoms/TableLink";
import { AlertConditionCard } from "../../molecules/AlertConditionCard";
import type { AlertConditionConfig } from "../../../services/delivery/alertCondition.types";
import type { AlertConditionsSectionProps } from "./AlertConditionsSection.types";
import { useAlertConditionCardInternalState } from "./useAlertConditionCardInternalState";
import {
  CardList,
  Header,
  Heading,
  MatchGroup,
  MatchLabel,
  MatchPicker,
  NotificationText,
  OperatorSeparator,
  Section,
} from "./AlertConditionsSection.styles";

const MATCH_OPTIONS = [
  { id: "and", label: "And" },
  { id: "or", label: "Or" },
];

export const getConditionTitle = (index: number, count: number): string =>
  count === 1 ? "Condition" : `Condition ${index + 1}`;

export const getConditionNotificationText = (
  count: number,
  matchMode: AlertConditionsSectionProps["matchMode"],
): string => {
  if (count === 1) return "You’ll be notified when this condition is met.";
  return matchMode === "and"
    ? "You’ll be notified when all of these conditions are met."
    : "You’ll be notified when either of these conditions is met.";
};

interface ConditionCardControllerProps {
  condition: AlertConditionConfig;
  title: string;
  metricOptions: AlertConditionsSectionProps["metricOptions"];
  comparatorOptions: AlertConditionsSectionProps["comparatorOptions"];
  currentMetricText?: string;
  onUpdate: AlertConditionsSectionProps["onUpdate"];
  onRemove: AlertConditionsSectionProps["onRemove"];
}

const ConditionCardController: React.FC<
  ConditionCardControllerProps
> = ({
  condition,
  title,
  metricOptions,
  comparatorOptions,
  currentMetricText,
  onUpdate,
  onRemove,
}) => {
  const { metricDropdown, comparatorDropdown } =
    useAlertConditionCardInternalState();
  const { id, ...value } = condition;

  return (
    <AlertConditionCard
      title={title}
      value={value}
      metricOptions={metricOptions}
      comparatorOptions={comparatorOptions}
      metricDropdown={metricDropdown}
      comparatorDropdown={comparatorDropdown}
      currentMetricText={currentMetricText}
      onChange={(patch) => onUpdate(id, patch)}
      onRemove={() => onRemove(id)}
    />
  );
};

export const AlertConditionsSection: React.FC<
  AlertConditionsSectionProps
> = ({
  conditions,
  maxConditions,
  matchMode,
  metricOptions,
  comparatorOptions,
  currentMetricTextByMetric = {},
  onAdd,
  onUpdate,
  onRemove,
  onMatchModeChange,
}) => {
  const multiple = conditions.length > 1;
  const addDisabled = conditions.length >= maxConditions;

  return (
    <Section aria-label="Conditions">
      <Header>
        <Heading>Conditions</Heading>
        <TableLink onClick={onAdd} disabled={addDisabled}>
          Add condition
        </TableLink>
      </Header>

      {multiple ? (
        <MatchGroup>
          <MatchLabel>Match</MatchLabel>
          <MatchPicker
            options={MATCH_OPTIONS}
            value={matchMode}
            variant="form"
            ariaLabel="Condition match mode"
            onChange={(mode) =>
              onMatchModeChange(mode as typeof matchMode)
            }
          />
        </MatchGroup>
      ) : null}

      <CardList>
        {conditions.map((condition, index) => (
          <React.Fragment key={condition.id}>
            {index > 0 ? (
              <OperatorSeparator>
                {matchMode.toUpperCase()}
              </OperatorSeparator>
            ) : null}
            <ConditionCardController
              condition={condition}
              title={getConditionTitle(index, conditions.length)}
              metricOptions={metricOptions}
              comparatorOptions={comparatorOptions}
              currentMetricText={
                condition.metric
                  ? currentMetricTextByMetric[condition.metric]
                  : undefined
              }
              onUpdate={onUpdate}
              onRemove={onRemove}
            />
          </React.Fragment>
        ))}
      </CardList>

      <NotificationText aria-live="polite">
        {getConditionNotificationText(conditions.length, matchMode)}
      </NotificationText>
    </Section>
  );
};

export default AlertConditionsSection;
